import React, {useEffect, useRef, useCallback} from 'react'
import {useTranslation, Trans} from 'react-i18next'
import {useParams, useHistory, useLocation} from 'react-router-dom'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {Container, Grid, Box} from '@mui/material'
import {
  getProductLineColors,
  PGButton,
  PGTypography,
} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import {BookingBreadcrumb} from '@progyny/components/BookingBreadcrumb'
import CalendarEmbed from '@progyny/components/CalendarEmbed'
import JumboTitle from '@progyny/components/JumboTitle'
import {
  useConsultationsSessionBookingWindowQuery,
  useConsultationsProgramDetailQuery,
  useSetSessionSkippedMutation,
  useSessionBookingParamsQuery,
} from '@progyny/generated/graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {ConsultationCard} from '@progyny/pages/Consultations/components/ConsultationCard'
import ConsultationOverlayHeader from '@progyny/pages/Consultations/components/ConsultationOverlayHeader'
import {showConsultationRescheduledToast} from '@progyny/pages/Consultations/components/ConsultationToast'
import {useMeStore} from '@progyny/store/meStore'
import {buildProgramSessionMetadata} from '@progyny/utils/calendarMetadata'
import {getBookingFieldsForProgram} from '@progyny/utils/programBookingFields'
import {shouldLockAttendeeFields} from '@progyny/utils/programLockAttendeeFields'
import {buildRoutingFormResponses} from '@progyny/utils/routingFormResponses'

interface SessionBookingParams {
  program: string
  sessionid: string
}

const useStyles = makeStyles()((theme) => ({
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '72px',
    cursor: 'pointer',
    color: '#FFFFFF',
    textDecoration: 'none',
    '&:hover': {
      opacity: 0.8,
    },
  },
  backText: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    color: '#FFFFFF',
  },
}))

const SessionBooking: React.ComponentType = () => {
  const {sessionid, program: programId} = useParams<SessionBookingParams>()
  const {productLine, isFertility} = useProductLine()
  const colors = getProductLineColors(productLine)
  const {classes} = useStyles()
  const {t} = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const user = useMeStore((s) => s.user)
  const countryCode = useMeStore((s) => s.countryCode)
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const [pendingConfirmations, setPendingConfirmations] = React.useState<
    Set<string>
  >(new Set())
  const isLanguageChanged =
    user?.member?.language !== localStorage.getItem('ui_language')

  const {data: programData, refetch: refetchProgram} =
    useConsultationsProgramDetailQuery({
      variables: {slug: programId},
      skip: !programId,
    })

  const program = programData?.consultations?.program
  const programEnrollment =
    programData?.consultations?.myProgramEnrollments?.find(
      (e) => e?.program?.id === program?.id
    )

  // Get query params - if rescheduleUid exists, we're in reschedule mode
  const searchParams = new URLSearchParams(location.search)
  const singleSessionParam = searchParams.get('session')
  const rescheduleUid = searchParams.get('rescheduleUid')

  // Redirect to program page if not enrolled (allow reschedules even without enrollment)
  useEffect(() => {
    if (program && !programEnrollment && !rescheduleUid) {
      history.push(`/consultations/program/${programId}`)
    }
  }, [program, programEnrollment, rescheduleUid, history, programId])

  const calculatedSessionNumbers = React.useMemo(() => {
    // Single session booking (ON_DEMAND or individual session)
    if (singleSessionParam) {
      return [Number(singleSessionParam)]
    }

    // Multi-session booking (PRE_BOOK_ALL): find all unbooked and unskipped sessions (including optional)
    if (program?.bookingStrategy === 'PRE_BOOK_ALL' && programEnrollment) {
      const bookedSessionIds = new Set(
        programEnrollment.sessionStatuses
          ?.filter(
            (ss) => ss?.status === 'BOOKED' || ss?.status === 'COMPLETED'
          )
          .map((ss) => ss?.session?.id)
      )

      const skippedSessionIds = new Set(
        programEnrollment.sessionStatuses
          ?.filter((ss) => ss?.status === 'SKIPPED')
          .map((ss) => ss?.session?.id)
      )

      return (
        program.sessions
          ?.filter(
            (s) => !bookedSessionIds.has(s?.id) && !skippedSessionIds.has(s?.id)
          )
          .map((s) => s?.sessionNumber)
          .filter((n): n is number => typeof n === 'number') || []
      )
    }

    // Fallback to old route param for backward compatibility
    return sessionid ? [Number(sessionid)] : []
  }, [singleSessionParam, program, programEnrollment, sessionid])

  // Freeze session numbers when booking flow starts to prevent array shifting during polling
  const initialSessionNumbers = useRef<number[]>([])
  useEffect(() => {
    if (
      calculatedSessionNumbers.length > 0 &&
      initialSessionNumbers.current.length === 0
    ) {
      initialSessionNumbers.current = calculatedSessionNumbers
    }
  }, [calculatedSessionNumbers])

  const sessionNumbers =
    initialSessionNumbers.current.length > 0
      ? initialSessionNumbers.current
      : calculatedSessionNumbers

  // Map session numbers to session IDs using program data
  const sessionIds = sessionNumbers
    .map((num) => program?.sessions?.find((s) => s?.sessionNumber === num)?.id)
    .filter(Boolean) as string[]

  const currentSessionId = sessionIds[currentIndex]

  const {
    data: sessionData,
    loading: sessionLoading,
    refetch: refetchSession,
  } = useConsultationsSessionBookingWindowQuery({
    variables: {sessionId: currentSessionId},
    skip: !currentSessionId,
  })

  const session = sessionData?.consultations?.session
  const bookingWindow = session?.bookingWindow

  // Get booking parameters for PCA routing and language selection
  const {data: bookingParamsData, refetch: refetchBookingParams} =
    useSessionBookingParamsQuery({
      variables: {sessionId: Number(currentSessionId)},
      skip: !currentSessionId,
    })

  const bookingParams = bookingParamsData?.consultations?.sessionBookingParams

  // Track whether we're waiting for PCA assignment from previous booking's webhook
  const [waitingForPCAAssignment, setWaitingForPCAAssignment] =
    React.useState(false)

  // For subsequent sessions, wait for PCA assignment before showing calendar
  // This prevents race condition where Session 2 loads before Session 1's webhook processes
  useEffect(() => {
    // Only relevant for subsequent sessions (not first session)
    const isSubsequentSession = currentIndex > 0

    if (!isSubsequentSession || !bookingParams) return

    const hasRoutedPCAs =
      bookingParams.routedTeamMemberIds &&
      bookingParams.routedTeamMemberIds.length > 0

    // If this is a subsequent session but no PCA is assigned yet, poll for webhook completion
    if (!hasRoutedPCAs && pendingConfirmations.size > 0) {
      setWaitingForPCAAssignment(true)

      const pollInterval = setInterval(async () => {
        // Refetch booking params to get updated PCA assignment
        const result = await refetchBookingParams()
        const updatedParams = result.data?.consultations?.sessionBookingParams

        if (
          updatedParams?.routedTeamMemberIds &&
          updatedParams.routedTeamMemberIds.length > 0
        ) {
          // PCA assignment confirmed, stop polling
          setWaitingForPCAAssignment(false)
          clearInterval(pollInterval)
        }
      }, 1000) // Poll every second

      // Timeout after 10 seconds to prevent infinite polling
      const timeout = setTimeout(() => {
        clearInterval(pollInterval)
        setWaitingForPCAAssignment(false)
        console.warn(
          'Timeout waiting for PCA assignment from previous booking webhook'
        )
      }, 10000)

      return () => {
        clearInterval(pollInterval)
        clearTimeout(timeout)
      }
    } else {
      setWaitingForPCAAssignment(false)
    }
  }, [
    currentIndex,
    bookingParams,
    pendingConfirmations.size,
    refetchBookingParams,
  ])

  const [setSessionSkipped] = useSetSessionSkippedMutation()

  const handleSkipSession = useCallback(async () => {
    if (!programEnrollment?.id || !currentSessionId) return

    try {
      const result = await setSessionSkipped({
        variables: {
          programEnrollmentId: String(programEnrollment.id),
          sessionId: currentSessionId,
          skipped: true,
        },
      })

      if (result.data?.setSessionSkipped?.success) {
        // Move to next session or redirect if done
        if (currentIndex < sessionIds.length - 1) {
          setCurrentIndex(currentIndex + 1)
        } else {
          history.push(`/consultations/program/${programId}`)
        }
      }
    } catch (err) {
      console.error('Skip session error:', err)
    }
  }, [
    programEnrollment?.id,
    currentSessionId,
    setSessionSkipped,
    currentIndex,
    sessionIds.length,
    history,
    programId,
  ])

  // Store the original referrer when first entering the session booking section
  const originalReferrer = useRef<string | null>(null)

  useEffect(() => {
    if (originalReferrer.current === null) {
      if (history.location.state && (history.location.state as any).referrer) {
        originalReferrer.current = (history.location.state as any).referrer
      } else {
        originalReferrer.current = `/consultations/program/${programId}`
      }
    }
  }, [location.pathname, history.location.state, programId])

  const handleBackClick = () => {
    if (originalReferrer.current) {
      history.push(originalReferrer.current)
    } else {
      history.goBack()
    }
  }

  const handleBookingSuccessful = useCallback(() => {
    // Track this session as pending webhook confirmation
    setPendingConfirmations((prev) => new Set([...prev, currentSessionId]))

    if (rescheduleUid) {
      showConsultationRescheduledToast()
    }
  }, [currentSessionId, rescheduleUid])

  const handleDone = useCallback(() => {
    // Move to next session (confirmation happens in background via webhook)
    if (currentIndex < sessionIds.length - 1) {
      setCurrentIndex(currentIndex + 1)
    } else {
      // All sessions booked, redirect to program page
      history.push(`/consultations/program/${programId}`)
    }
  }, [currentIndex, sessionIds.length, history, programId])

  // Poll for breadcrumb updates while there are pending webhook confirmations
  useEffect(() => {
    if (pendingConfirmations.size === 0) return

    const interval = setInterval(async () => {
      await refetchProgram()

      // Check which pending sessions are now confirmed
      const confirmedSessionIds = new Set(
        programEnrollment?.sessionStatuses
          ?.filter(
            (ss) => ss?.status === 'BOOKED' || ss?.status === 'COMPLETED'
          )
          .map((ss) => ss?.session?.id)
      )

      // Remove confirmed sessions from pending set
      const stillPending = Array.from(pendingConfirmations).filter(
        (id) => !confirmedSessionIds.has(id)
      )

      if (stillPending.length !== pendingConfirmations.size) {
        setPendingConfirmations(new Set(stillPending))
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [pendingConfirmations, programEnrollment?.sessionStatuses, refetchProgram])

  useEffect(() => {
    if (isLanguageChanged) {
      refetchProgram()
      refetchSession()
      refetchBookingParams()
    }
  }, [isLanguageChanged, refetchProgram, refetchSession, refetchBookingParams])

  const sessions =
    programEnrollment?.sessionStatuses?.map((sessionStatus) => ({
      sessionIndex: String(sessionStatus?.session?.sessionNumber || ''),
      title: sessionStatus?.session?.title || '',
      isBooked:
        sessionStatus?.status === 'BOOKED' ||
        sessionStatus?.status === 'COMPLETED',
    })) || []

  const currentSessionNumber = program?.sessions?.find(
    (s) => s?.id === currentSessionId
  )?.sessionNumber
  const activeSessionIndex = currentSessionNumber
    ? String(currentSessionNumber)
    : ''

  const isCalendarVisible = Boolean(
    !waitingForPCAAssignment &&
    !sessionLoading &&
    session?.calcomEventType &&
    bookingWindow
  )

  const bookingCards = (
    <Grid container mt={5}>
      {waitingForPCAAssignment && (
        <ConsultationCard padding='lg' borderRadius='lg'>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              gap: 2,
            }}
          >
            <PGTypography>
              <Trans i18nKey='consultations_session_booking_confirming_your_booking' />
            </PGTypography>
          </Box>
        </ConsultationCard>
      )}
      {isCalendarVisible && session?.calcomEventType && bookingWindow && (
        <ConsultationCard padding='none' borderRadius='lg'>
          <CalendarEmbed
            calLink={session?.calcomEventType}
            title={session.title ?? undefined}
            onBookingSuccessful={handleBookingSuccessful}
            onDone={handleDone}
            rescheduleUid={rescheduleUid ?? undefined}
            prefillData={{
              email: user?.email || '',
              name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            }}
            metadata={{
              ...buildProgramSessionMetadata(
                user?.id || '',
                currentSessionId,
                programEnrollment?.id || ''
              ),
              ...(typeof bookingParams?.metadata === 'object' &&
              bookingParams?.metadata !== null
                ? (bookingParams.metadata as Record<string, string>)
                : {}),
            }}
            additionalContent={session.bookingDescription ?? undefined}
            minDate={bookingWindow.startDate ?? undefined}
            maxDate={bookingWindow.endDate ?? undefined}
            routedTeamMemberIds={
              bookingParams?.routedTeamMemberIds?.filter(
                (id): id is number => id !== null
              ) ?? undefined
            }
            language={bookingParams?.language ?? undefined}
            languageLocked={bookingParams?.languageLocked ?? false}
            bookingFields={
              program?.slug
                ? getBookingFieldsForProgram(program.slug)
                : undefined
            }
            lockAttendeeFields={
              program?.slug ? shouldLockAttendeeFields(program.slug) : false
            }
            routingFormResponses={buildRoutingFormResponses(countryCode)}
            onBack={handleBackClick}
            startingStep={2}
          />

          {/* Show Skip button for optional sessions in multi-session flow */}
          {session.required === false && sessionIds.length > 1 && (
            <Box
              sx={{
                borderTop: '1px solid #eee',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                padding: '8px 0',
              }}
            >
              <PGButton
                variant='outlined'
                label={t('consultations_session_booking_skip_this_session')}
                onClick={handleSkipSession}
                size='large'
              />
            </Box>
          )}
        </ConsultationCard>
      )}
    </Grid>
  )

  return (
    <Container maxWidth='xl'>
      {isFertility ? (
        <JumboTitle
          title={isCalendarVisible ? undefined : program?.title}
          description={isCalendarVisible ? undefined : program?.description}
          disableContentWrapper
          forceDefaultHeight={isCalendarVisible}
        >
          <Grid container pb={10}>
            {bookingCards}
          </Grid>
        </JumboTitle>
      ) : (
        <>
          <ConsultationOverlayHeader
            title={isCalendarVisible ? undefined : program?.title}
            description={isCalendarVisible ? undefined : program?.description}
            followingContentMode='contentOnTop'
            colors={colors}
          />
          <Grid container pb={10}>
            <PGButton
              disableRipple
              variant='text'
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              className={classes.backLink}
              label={t('consultations_onboarding_back_to_overview')}
            />

            <BookingBreadcrumb
              sessions={sessions}
              activeSessionIndex={activeSessionIndex}
            />

            {bookingCards}
          </Grid>
        </>
      )}
    </Container>
  )
}

export default SessionBooking
